/*
===============================================================================================================
                                 Copyright 2024-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { LocaleContext } from '@/contexts/locale-context';
import { formatCurrency } from '@/utilities/currency';
import TextDiv from '@/components/text-div';
import { getStaticCdnPath } from '@/utilities/static-build';

/**
 * A small component to show the item in the process of being returned
 *
 * @param {boolean} disabled grays text if disabled
 * @param {string} selectorId A string to pass to the parent div to wrap
 * @param {string} imageHeight A string to pass in to set the image height e.g. "105px"
 *
 * @return {JSX.Element}
 */
export default function ReturnItem({
  disabled,
  selectorId,
  product,
  imageHeight,
}) {
  const [flexDirection, setFlexDirection] = useState(null);
  const { translate, textDirection, getIntlLocale } = useContext(LocaleContext);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  /**
   * Swap over which direction of the print option to display based on the language direction
   */
  useEffect(() => {
    if (textDirection === 'rtl') {
      setFlexDirection('row-reverse');
    } else {
      setFlexDirection('row');
    }
  }, [textDirection]);

  const {
    productName,
    price,
    priceCurrency,
    imageUrl,
    size,
    color,
    errorDescription,
  } = { ...product };

  const hasPrice = priceCurrency && price;

  return (
    <>
      <div id={selectorId} className="summary-wrapper">
        <div className="summary-flex-wrapper">
          <div className="image-container">
            {imageUrl ? (
              <img src={`${getStaticCdnPath()}${imageUrl}`} alt={productName} className={`item-image${disabled ? ' disabled' : ''}`} />
            ) : (
              <div className="item-image empty-state" />
            )}
          </div>
          <div className="summary-flex-wrapper summary-text-fields-wrapper">
            <div className={`summary-description-wrapper${textDirection === 'rtl' ? ' rtl' : ''}`}>
              <TextDiv selectorId={`${selectorId}-item-product-name`} className={`global-label-link--bold${disabled ? '--disabled' : ''}`}>
                {productName}
              </TextDiv>
            </div>
            <div className={`summary-details-wrapper${textDirection === 'rtl' ? ' rtl' : ''}`} dir={textDirection}>
              {!isMobile && hasPrice && (
                <TextDiv selectorId={`${selectorId}-item-price`} className={`global-paragraph-text${disabled ? '--disabled' : ''}`}>
                  {`${formatCurrency(priceCurrency, price, getIntlLocale())}`}
                </TextDiv>
              )}
              {size && (
                <TextDiv selectorId={`${selectorId}-item-size`} className={`global-paragraph-text${disabled ? '--disabled' : ''}`}>
                  {`${translate('constants.size')} ${size}`}
                </TextDiv>
              )}
              {color && (
                <TextDiv selectorId={`${selectorId}-item-color`} className={`global-paragraph-text${disabled ? '--disabled' : ''}`}>
                  {color}
                </TextDiv>
              )}
              {isMobile && hasPrice && (
                <TextDiv selectorId={`${selectorId}-item-price`} className={`global-paragraph-text${disabled ? '--disabled' : ''} align-self-end`}>
                  {`${formatCurrency(priceCurrency, price, getIntlLocale())}`}
                </TextDiv>
              )}
              {disabled && errorDescription && (
                <TextDiv selectorId={`${selectorId}-item-error`} className="global-paragraph-text--error align-self-end">
                  {translate(errorDescription)}
                </TextDiv>
              )}
            </div>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .empty-state {
            background-color: #f3f3f3;
            height: 80px;
            width: 80px;
            border-radius: 4px;
          }

          .item-image {
            display: block;
            margin-right: ${textDirection === 'rtl' ? '' : '18px'};
            max-height: ${imageHeight};
            max-width: 80px;
          }

          .item-image.disabled {
            filter: gray;
            filter: grayscale(1);
          }

          .image-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
  
          .summary-wrapper {
            display: flex;
            flex-direction: ${flexDirection};
            justify-content: space-between;
            width: 100%;
          }
  
          .summary-flex-wrapper {
            display: flex;
            flex-direction: ${flexDirection};
            flex-grow: 1;
          }

          .summary-description-wrapper {
            flex-grow: 1;
            min-width: 90px;
            padding: 12px 18px 0 10px;
            text-align: ${textDirection === 'rtl' ? 'right' : 'left'};
            word-break: break-word;
          }

          .summary-details-wrapper {
            display: flex;
            flex-direction: column;
            padding-top: 12px;
            text-align: right;
          }

          .summary-details-wrapper.rtl {
            text-align: left;
          }

          @media only screen and (max-width: 768px) {
            .summary-text-fields-wrapper {
              flex-direction: column;
            }

            .summary-details-wrapper {
              text-align: left;
            }

            .summary-details-wrapper.rtl {
              padding-right: 18px;
              text-align: right;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          @media only screen and (max-width: 768px) {
            .align-self-end {
              align-self: flex-end;
            }
          }
        `}
      </style>
    </>
  );
}

ReturnItem.defaultProps = {
  disabled: false,
  imageHeight: '105px',
};

ReturnItem.propTypes = {
  disabled: PropTypes.bool,
  selectorId: PropTypes.string.isRequired,
  imageHeight: PropTypes.string,
  product: PropTypes.shape({
    productName: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    priceCurrency: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    size: PropTypes.string,
    color: PropTypes.string,
    quantity: PropTypes.number,
    errorDescription: PropTypes.string,
  }).isRequired,
};
